import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useParams, useLocation } from 'react-router-dom';
import { Save, Trash2 } from 'lucide-react';
import BackButton from '../components/BackButton';
import { useNotification } from '../hooks/useNotification';
import BookNavigationTabs from '../components/BookNavigationTabs';
import { useSyncLanguageUrl } from '../hooks/useSyncLanguageUrl';

const LANGUAGE_LABELS = {
  en: 'English',
  de: 'German',
  es: 'Spanish',
  nl: 'Dutch',
  ru: 'Russian',
  it: 'Italian',
  fr: 'French',
  pl: 'Polish',
  tr: 'Turkish',
};

const COVER_SIDES = [
  { value: 'front', label: 'Front Cover' },
  { value: 'back', label: 'Back Cover' },
];

const syncEnglishFromResponse = (
  texts,
  setters,
  dirtyFlags,
  { forceFront = false, forceBack = false } = {},
) => {
  const frontEntry =
    (texts.front || []).find((entry) => entry.language === 'en') || null;
  const backEntry =
    (texts.back || []).find((entry) => entry.language === 'en') || null;

  if (forceFront || !dirtyFlags.isEnglishFrontDirty) {
    setters.setEnglishFrontText(frontEntry?.text || '');
  }
  if (forceBack || !dirtyFlags.isEnglishBackDirty) {
    setters.setEnglishBackText(backEntry?.text || '');
  }
};

function BookCoverTextsPage() {
  const { bookId } = useParams();
  const location = useLocation();
  const [bookName, setBookName] = useState('');
  const [coverTexts, setCoverTexts] = useState({ front: [], back: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [englishFrontText, setEnglishFrontText] = useState('');
  const [englishBackText, setEnglishBackText] = useState('');
  const [isEnglishFrontDirty, setEnglishFrontDirty] = useState(false);
  const [isEnglishBackDirty, setEnglishBackDirty] = useState(false);
  const [pageLanguage, setPageLanguage] = useState('es_de');
  const { notify, Notification } = useNotification();

  useSyncLanguageUrl(pageLanguage);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const langParam = searchParams.get('plang');
    if (langParam) {
      setPageLanguage(langParam);
    }
  }, [location.search]);

  const languageLabel = (language) =>
    LANGUAGE_LABELS[language] || language.toUpperCase();

  const fetchCoverTexts = useCallback(async () => {
    if (!bookId) return;
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`/api/books/${bookId}/cover-texts`);
      const { book_name, cover_texts } = response.data;
      const normalized = {
        front: cover_texts?.front || [],
        back: cover_texts?.back || [],
      };
      setBookName(book_name || '');
      setCoverTexts(normalized);
      setEnglishFrontDirty(false);
      setEnglishBackDirty(false);
      syncEnglishFromResponse(
        normalized,
        { setEnglishFrontText, setEnglishBackText },
        { isEnglishFrontDirty: false, isEnglishBackDirty: false },
        {
          forceFront: true,
          forceBack: true,
        },
      );
    } catch (err) {
      console.error('Failed to fetch cover texts', err);
      setError('Failed to load cover translations.');
    } finally {
      setLoading(false);
    }
  }, [bookId]);

  useEffect(() => {
    fetchCoverTexts();
  }, [bookId, fetchCoverTexts]);

  const handleSave = async (side, language, text) => {
    try {
      const response = await axios.put(
        `/api/books/${bookId}/cover-texts/${side}/${language}`,
        { text },
      );
      const { book_name: updatedName, cover_texts: updatedTexts } =
        response.data;
      const normalized = {
        front: updatedTexts?.front || [],
        back: updatedTexts?.back || [],
      };
      setBookName(updatedName || '');
      setCoverTexts(normalized);
      syncEnglishFromResponse(
        normalized,
        { setEnglishFrontText, setEnglishBackText },
        { isEnglishFrontDirty, isEnglishBackDirty },
        {
          forceFront: side === 'front' && language === 'en',
          forceBack: side === 'back' && language === 'en',
        },
      );
      if (side === 'front' && language === 'en') {
        setEnglishFrontDirty(false);
      }
      if (side === 'back' && language === 'en') {
        setEnglishBackDirty(false);
      }
      notify('Cover text saved.');
    } catch (err) {
      console.error('Failed to save cover text', err);
      notify('Failed to save cover text.', 'error');
    }
  };

  const handleDelete = async (side, language) => {
    const confirmed = window.confirm(
      `Remove the ${languageLabel(language)} ${side} cover text?`,
    );
    if (!confirmed) return;

    try {
      const response = await axios.delete(
        `/api/books/${bookId}/cover-texts/${side}/${language}`,
      );
      const { book_name: updatedName, cover_texts: updatedTexts } =
        response.data;
      const normalized = {
        front: updatedTexts?.front || [],
        back: updatedTexts?.back || [],
      };
      setBookName(updatedName || '');
      setCoverTexts(normalized);
      syncEnglishFromResponse(
        normalized,
        { setEnglishFrontText, setEnglishBackText },
        { isEnglishFrontDirty, isEnglishBackDirty },
      );
      notify('Cover text removed.');
    } catch (err) {
      console.error('Failed to delete cover text', err);
      notify('Failed to remove cover text.', 'error');
    }
  };

  const handleEnglishSave = async (side) => {
    const textToSave = side === 'front' ? englishFrontText : englishBackText;
    await handleSave(side, 'en', textToSave);
  };

  const renderTranslations = (side) =>
    (coverTexts[side] || [])
      .filter((entry) => entry.language !== 'en')
      .map(({ language, text }) => (
        <div
          key={`${side}-${language}`}
          className="border border-gray-200 rounded p-4 bg-gray-50"
        >
          <div className="flex justify-between items-center mb-2">
            <span className="font-medium">{languageLabel(language)}</span>
            <div className="flex gap-2">
              <button
                onClick={() => handleSave(side, language, text)}
                className="flex items-center gap-1 px-3 py-1 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-100"
              >
                <Save size={16} />
                Save
              </button>
              <button
                onClick={() => handleDelete(side, language)}
                className="flex items-center gap-1 px-3 py-1 bg-red-500 text-white rounded shadow-sm hover:bg-red-600"
              >
                <Trash2 size={16} />
                Remove
              </button>
            </div>
          </div>
          <textarea
            value={text}
            rows={4}
            className="w-full border border-gray-300 rounded p-2"
            onChange={(event) => {
              const newValue = event.target.value;
              setCoverTexts((prev) => ({
                ...prev,
                [side]: prev[side].map((entry) =>
                  entry.language === language
                    ? { ...entry, text: newValue }
                    : entry,
                ),
              }));
            }}
          />
        </div>
      ));

  return (
    <>
      <Notification />
      <div className="max-w-6xl mx-auto bg-white shadow-lg rounded-lg p-6 mt-10">
        <BookNavigationTabs
          bookId={bookId}
          currentLanguage={pageLanguage}
          languageSelector={
            <>
              <span className="text-sm text-gray-600">Current Profile:</span>
              <select
                value={pageLanguage}
                onChange={(e) => setPageLanguage(e.target.value)}
                className="border border-gray-300 rounded py-1 px-2 text-sm"
              >
                <option value="es_de">ES-DE</option>
                <option value="es_en">ES-EN</option>
                <option value="es_ru">ES-RU</option>
                <option value="es_it">ES-IT</option>
                <option value="es_tr">ES-TR</option>
                <option value="es_fr">ES-FR</option>
                <option value="es_pl">ES-PL</option>
                <option value="de_nl">DE-NL</option>
                <option value="de_ru">DE-RU</option>
                <option value="de_en">DE-EN</option>
                <option value="de_it">DE-IT</option>
                <option value="de_fr">DE-FR</option>
                <option value="de_tr">DE-TR</option>
                <option value="de_pl">DE-PL</option>
                <option value="en_nl">EN-NL</option>
                <option value="en_pl">EN-PL</option>
                <option value="en_it">EN-IT</option>
                <option value="en_fr">EN-FR</option>
                <option value="en_tr">EN-TR</option>
                <option value="en_ru">EN-RU</option>
              </select>
            </>
          }
        />

        <div className="flex items-center text-xl font-semibold mb-6">
          <BackButton className="p-2 rounded hover:bg-gray-200" />
          <span className="text-center flex-grow">
            {bookName || 'Cover Translations'}
          </span>
        </div>

        {loading && <p className="text-gray-600">Loading cover texts...</p>}
        {error && <p className="text-red-500">{error}</p>}

        {!loading && !error && (
          <div className="space-y-8">
            <section>
              <h2 className="text-lg font-semibold mb-2">Add Cover Text</h2>
              <p className="text-sm text-gray-600 mb-4">
                Add or edit the English cover text. Other language versions
                appear below.
              </p>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="border border-gray-200 rounded p-4 bg-gray-50">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">Front Cover (English)</span>
                    <button
                      onClick={() => handleEnglishSave('front')}
                      className="flex items-center gap-1 px-3 py-1 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-100"
                    >
                      <Save size={16} />
                      Save
                    </button>
                  </div>
                  <textarea
                    value={englishFrontText}
                    rows={4}
                    className="w-full border border-gray-300 rounded p-2"
                    placeholder="Enter the front cover text in English"
                    onChange={(event) => {
                      setEnglishFrontText(event.target.value);
                      setEnglishFrontDirty(true);
                    }}
                  />
                </div>
                <div className="border border-gray-200 rounded p-4 bg-gray-50">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">Back Cover (English)</span>
                    <button
                      onClick={() => handleEnglishSave('back')}
                      className="flex items-center gap-1 px-3 py-1 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-100"
                    >
                      <Save size={16} />
                      Save
                    </button>
                  </div>
                  <textarea
                    value={englishBackText}
                    rows={4}
                    className="w-full border border-gray-300 rounded p-2"
                    placeholder="Enter the back cover text in English"
                    onChange={(event) => {
                      setEnglishBackText(event.target.value);
                      setEnglishBackDirty(true);
                    }}
                  />
                </div>
              </div>
            </section>

            {COVER_SIDES.map(({ value, label }) => {
              const translations = renderTranslations(value);
              return (
                <section key={value}>
                  <h2 className="text-lg font-semibold mb-3">
                    {label} – Other Languages
                  </h2>
                  <div className="space-y-4">
                    {translations.length > 0 ? (
                      translations
                    ) : (
                      <p className="text-sm text-gray-500">
                        No {label.toLowerCase()} translations yet.
                      </p>
                    )}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

export default BookCoverTextsPage;
